import styled, { DefaultTheme } from 'styled-components';
import { getHorizontalInputPadding } from '@mews-ui/theme';
import { Disableable, getTextPresetCss, Size } from '@optimus-web/common';
import {
    BORDER_RADIUS,
    Component,
    Decision,
    getComponentVariableToken,
    OPACITY,
    SPACING,
    TEXT_PRESET_KEY,
} from '@optimus-web/local-tokens';
import theme from '@optimus-web/theme';
import { BareIconButton } from '../BareIconButton';

const getInputToken = getComponentVariableToken(Component.Input, 'variant');
const getInputPlaceholderToken = getComponentVariableToken(Component.InputPlaceholder, 'variant');
const getInputClearButtonToken = getComponentVariableToken(Component.InputClearButton, 'variant');
const getInputIndicativeIconToken = getComponentVariableToken(Component.InputIndicativeIconDecision, 'variant');

interface InputContainerProps extends Disableable {
    inputSize?: Size;
    readOnly?: boolean;
    hasLeftPadding?: boolean;
    hasRightPadding?: boolean;
}

export const InputContainer = styled.div<InputContainerProps>`
    ${(props) =>
        props.readOnly &&
        !props.disabled &&
        `
        cursor: pointer;
    `}
    height: 100%;
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    box-sizing: border-box;

    ${(props) =>
        props.hasLeftPadding &&
        `
        padding-left: ${props.inputSize === 'small' ? SPACING[100] : SPACING[200]}px;
    `}
    ${(props) =>
        props.hasRightPadding &&
        `
        padding-right: ${props.inputSize === 'small' ? SPACING[100] : SPACING[200]}px;
    `}
`;

interface InputElementProps {
    inputSize?: Size;
    size?: Size;
    smallLeftSpacing?: boolean;
    smallRightSpacing?: boolean;
    alignRight?: boolean;
}

export const InputElement = styled.input<InputElementProps>`
    ${(props) =>
        getTextPresetCss(
            props.inputSize === 'small' || props.size === 'small' ? TEXT_PRESET_KEY['200r'] : TEXT_PRESET_KEY['300r']
        )}
    color: ${(props) => getInputToken(Decision.Color, { state: props.disabled ? 'disabled' : undefined })};
    ${(props) =>
        props.readOnly &&
        `
        height: 100%;
    `}
    width: 100%;
    box-sizing: border-box;
    padding-left: ${(props) => (props.smallLeftSpacing || props.inputSize === 'small' ? SPACING[100] : SPACING[200])}px;
    padding-right: ${(props) =>
        props.smallRightSpacing || props.inputSize === 'small' ? SPACING[100] : SPACING[200]}px;
    border: 0;
    background: transparent;
    outline: none;
    display: block;
    margin: 0;
    padding-top: ${SPACING[100] - 1}px;
    padding-bottom: ${SPACING[100] - 1}px;
    border-radius: calc(${BORDER_RADIUS[100]} - 1px);

    ${(props) =>
        props.inputSize === 'small' &&
        `
        padding-top: 5px;
        padding-bottom: 4px;
    `}

    ${(props) =>
        props.inputSize === 'large' &&
        `
        padding-top: 11px;
        padding-bottom: 11px;
    `}

    appearance: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
    -webkit-text-fill-color: ${(props) =>
        getInputToken(Decision.Color, { state: props.disabled ? 'disabled' : undefined })};

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
    }

    &[type='number'] {
        appearance: textfield;
    }

    &:invalid {
        box-shadow: none;
    }

    &:-moz-submit-invalid {
        box-shadow: none;
    }

    &:-moz-ui-invalid {
        box-shadow: none;
    }

    &::-ms-clear {
        display: none;
        width: 0;
        height: 0;
    }
    text-align: ${(props) => (props.alignRight ? 'right' : 'left')};
`;

export const InputWrapper = styled.div`
    width: 100%;
    position: relative;
    justify-content: center;
    align-items: center;
    display: flex;
`;

export interface PlaceholderElementProps extends Disableable {
    children?: React.ReactNode;
    inputSize?: Size;
    smallLeftSpacing?: boolean;
    smallRightSpacing?: boolean;
    disabled?: boolean;
}

export const PlaceholderElement = styled.div<PlaceholderElementProps>`
    ${(props) => getTextPresetCss(TEXT_PRESET_KEY[props.inputSize === 'small' ? '200r' : '300r'])}
    color: ${(props) => getInputPlaceholderToken(Decision.Color, { state: props.disabled ? 'disabled' : undefined })};
    position: absolute;
    pointer-events: none;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    box-sizing: border-box;
    padding-left: ${(props) => (props.smallLeftSpacing || props.inputSize === 'small' ? SPACING[100] : SPACING[200])}px;
    padding-right: ${(props) =>
        props.smallRightSpacing || props.inputSize === 'small' ? SPACING[100] : SPACING[200]}px;
`;

export const getPaymentInputStyles = (themeProvided: DefaultTheme = theme) => `
    ${getTextPresetCss(TEXT_PRESET_KEY['300r'])({ theme: themeProvided })}
    color: ${getInputToken(Decision.Color)({ theme: themeProvided })};
    border: 0;
    display: block;
    outline: none;
    margin: 0;
    padding: 0 ${getHorizontalInputPadding({ theme: themeProvided })}px;
`;

export const ClearButton = styled(BareIconButton)`
    color: ${getInputClearButtonToken(Decision.Color)};

    &:disabled {
        opacity: ${OPACITY.disabled};
    }

    &:hover:enabled {
        color: ${getInputClearButtonToken(Decision.Color, { state: 'hover' })};
    }

    &:active:enabled {
        color: ${getInputClearButtonToken(Decision.Color, { state: 'active' })};
    }
`;

export const IndicativeIconWrapper = styled.div`
    color: ${getInputIndicativeIconToken(Decision.BackgroundColor)};
    height: 24px;
`;
